/**
 * Quantity input +/- buttons
 */
export default class QuantityInput {
    constructor() {
        this.QUANTITY_STEP = 1;
        this.QUANTITY_MAX = 10;
        this.debounce = false;
        this.init();
    }

    init() {
        document.querySelectorAll('.quantity').forEach(qtyWrapper => {
            this.initQuantityWrapper(qtyWrapper);
        });
    }

    initQuantityWrapper(qtyWrapper) {
        const input = qtyWrapper.querySelector('input.qty');
        if (!input) return;

        const form = input.closest('form.cart');
        const plusButton = qtyWrapper.querySelector('.quantity-plus');
        const minusButton = qtyWrapper.querySelector('.quantity-minus');

        // Initial setup
        let limits = {
            min: parseFloat(input.getAttribute('min')) || 1,
            max: this.QUANTITY_MAX
        };

        if (form && form.classList.contains('variations_form')) {
            // For variable products, update max based on selected variation
            limits = this.updateMaxQuantity(qtyWrapper, form);

            // Listen for variation changes
            form.addEventListener('change', (e) => {
                if (e.target.matches('.variations select')) {
                    limits = this.updateMaxQuantity(qtyWrapper, form);
                    this.updateButtonStates(input, plusButton, minusButton, limits);
                }
            });
        } else {
            // For simple products, use max from input attribute
            limits.max = Math.min(
                parseFloat(input.getAttribute('max')) || this.QUANTITY_MAX,
                this.QUANTITY_MAX
            );
        }

        // Update button states on input change
        input.addEventListener('change', () => {
            this.updateButtonStates(input, plusButton, minusButton, limits);
        });

        // Initial button state
        this.updateButtonStates(input, plusButton, minusButton, limits);

        // Minus button
        minusButton?.addEventListener('click', () => {
            this.updateQuantity(input, -this.QUANTITY_STEP, limits.min, limits.max);
            this.updateButtonStates(input, plusButton, minusButton, limits);
        });

        // Plus button
        plusButton?.addEventListener('click', () => {
            // Get current max in case it changed
            const currentMax = form && form.classList.contains('variations_form')
                ? this.getMaxQtyForVariation(form)
                : parseFloat(input.getAttribute('max')) || this.QUANTITY_MAX;
            this.updateQuantity(input, this.QUANTITY_STEP, limits.min, Math.min(currentMax, this.QUANTITY_MAX));
            this.updateButtonStates(input, plusButton, minusButton, limits);
        });
    }

    updateQuantity(input, delta, min, max) {
        if (this.debounce) return;

        const currentVal = parseFloat(input.value) || 0;
        const newVal = currentVal + delta;

        if (newVal >= min && newVal <= max) {
            input.value = newVal;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            this.debounce = true;
            setTimeout(() => this.debounce = false, 100);
        }
    }

    getMaxQtyForVariation(form) {
        // Get selected variation attributes
        const variationSelects = form.querySelectorAll('.variations select');
        const selectedAttributes = {};

        variationSelects.forEach(select => {
            if (select.value) {
                const attrName = select.name.replace('attribute_', '');
                selectedAttributes[attrName] = select.value;
            }
        });

        // Get variations data from form
        const variationsData = form.getAttribute('data-product_variations');
        if (!variationsData) return this.QUANTITY_MAX;

        try {
            const variations = JSON.parse(variationsData);

            // Find matching variation
            const matchingVariation = variations.find(variation => {
                return Object.keys(selectedAttributes).every(attrKey => {
                    const variationAttrKey = `attribute_${attrKey}`;
                    return variation.attributes[variationAttrKey] === selectedAttributes[attrKey];
                });
            });

            if (matchingVariation && matchingVariation.max_qty) {
                return parseInt(matchingVariation.max_qty);
            }
        } catch (e) {
            console.error('Failed to parse variations data:', e);
        }

        return this.QUANTITY_MAX;
    }

    updateMaxQuantity(qtyWrapper, form) {
        const input = qtyWrapper.querySelector('input.qty');
        if (!input) return { min: 1, max: this.QUANTITY_MAX };

        const maxQty = this.getMaxQtyForVariation(form);
        const min = parseFloat(input.getAttribute('min')) || 1;
        const max = Math.min(maxQty, this.QUANTITY_MAX);

        // Update max attribute
        input.setAttribute('max', max);

        // Ensure current value doesn't exceed max
        const currentVal = parseFloat(input.value) || min;
        if (currentVal > max) {
            input.value = max;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        return { min, max };
    }

    updateButtonStates(input, plusButton, minusButton, limits) {
        const currentVal = parseFloat(input.value) || limits.min;
        const currentMax = parseFloat(input.getAttribute('max')) || limits.max;

        // Update plus button
        if (plusButton) {
            if (currentVal >= currentMax) {
                plusButton.classList.add('disabled');
                plusButton.disabled = true;
            } else {
                plusButton.classList.remove('disabled');
                plusButton.disabled = false;
            }
        }

        // Update minus button
        if (minusButton) {
            if (currentVal <= limits.min) {
                minusButton.classList.add('disabled');
                minusButton.disabled = true;
            } else {
                minusButton.classList.remove('disabled');
                minusButton.disabled = false;
            }
        }
    }
}

// Export legacy function for backwards compatibility
export function initQuantityInputs() {
    new QuantityInput();
}